import { useMemo } from "react";

import { useIsReadOnlySurface } from "~/contexts/ReadOnlySurfaceContext";
import { useAuth } from "~/features/auth";

/**
 * ADR 0002 workaround. Bypasses every resource-permission check in the app
 * (`canRead`/`canCreate`/`canUpdate`/`canDelete`/`canPatchProperty`, both the
 * hook and `resourcePermissionsFromRoles` below) — the page composer, view
 * save/admin gating and workflow authoring all read this one constant, so
 * editing pages, views and workflows is open to every signed-in user while it
 * is on. Record-service writes stay enforced. `PUT ?lens=ui` checks no user
 * role (`useViewAdminPermissions`), so view saves are open on the server too.
 * This switch only stops the FE from hiding affordances.
 *
 * Expiry condition: delete this switch — flip role-based gating back on by
 * removing it, the role logic below is kept intact for exactly that — once
 * the access token carries coarse resource roles for `Nav*`,
 * `WorkflowDefinition` and `WorkflowRun`, AND an effective-permissions
 * (capabilities) read exists to answer per-resource grants instead of the
 * coarse-role heuristic below.
 * The gap: the token carries no coarse `Nav*`/`Workflow*` roles, and there is no
 * capabilities endpoint to ask instead. Both are requested from the owner of the
 * roles/permissions model.
 */
export const PERMISSION_CHECKS_DISABLED = true;

let hasWarnedPermissionChecksDisabled = false;

/**
 * ADR 0002 condition 3 (observability): a dev-only line, once per session,
 * naming the bypass so "is this still needed?" is answerable by running the
 * app rather than by reasoning about the constant above.
 */
const warnPermissionChecksDisabledOnce = (): void => {
  if (!import.meta.env.DEV || hasWarnedPermissionChecksDisabled) {
    return;
  }

  hasWarnedPermissionChecksDisabled = true;
  console.warn(
    "[permissions] PERMISSION_CHECKS_DISABLED is on — every resource-permission check answers " +
      "permitted regardless of role. The access token carries no coarse Nav*/Workflow* roles, " +
      "and there is no capabilities endpoint yet.",
  );
};

export interface ResourcePermissions {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canPatchProperty: (property: string) => boolean;
}

const ALL_PERMITTED: ResourcePermissions = {
  canRead: true,
  canCreate: true,
  canUpdate: true,
  canDelete: true,
  canPatchProperty: () => true,
};

/** Inside a read-only surface: reading is whatever the roles allow; every write is denied. */
const withoutWrites = (permissions: ResourcePermissions): ResourcePermissions => ({
  canRead: permissions.canRead,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
  canPatchProperty: () => false,
});

const COARSE_ROLE_RE = /^(GET|POST|PUT|PATCH|DELETE)-/;

/** True when the JWT listed at least one resource-method role. */
export const hasCoarsePermissionRoles = (roles: Iterable<string>): boolean => {
  for (const role of roles) {
    if (COARSE_ROLE_RE.test(role)) {
      return true;
    }
  }
  return false;
};

/**
 * The same rules as {@link useResourcePermissions}, without the hook — for a
 * caller that has to answer for N resources in one render (the table's edge
 * columns each point at a different linked resource).
 */
export const resourcePermissionsFromRoles = (
  roles: ReadonlySet<string>,
  resource: string,
): ResourcePermissions => {
  if (PERMISSION_CHECKS_DISABLED) {
    warnPermissionChecksDisabledOnce();

    return ALL_PERMITTED;
  }

  if (!hasCoarsePermissionRoles(roles)) {
    return ALL_PERMITTED;
  }

  const hasFullPatch = roles.has(`PATCH-${resource}`);

  return {
    canRead: roles.has(`GET-${resource}`) || roles.has(`GET-ALL-${resource}`),
    canCreate: roles.has(`POST-${resource}`),
    canUpdate: roles.has(`PUT-${resource}`) || hasFullPatch,
    canDelete: roles.has(`DELETE-${resource}`),
    canPatchProperty: (property: string) =>
      hasFullPatch || roles.has(`PATCH-${resource}-${property}`),
  };
};

/**
 * Evaluates CRUD + field-level permissions for a given resource based on
 * Keycloak roles extracted from the JWT token.
 *
 * Role naming convention (from BE):
 * - `GET-{Resource}`    → read
 * - `POST-{Resource}`   → create
 * - `PUT-{Resource}`    → update (full)
 * - `PATCH-{Resource}`  → update (partial, all properties)
 * - `PATCH-{Resource}-{Property}` → update a single property
 * - `DELETE-{Resource}` → delete
 *
 * When the token has no coarse permission roles, every action is treated as
 * permitted so a missing `roles` client-scope does not hide the product. The
 * backend still enforces. Once roles are present, missing grants deny.
 *
 * Inside a `ReadOnlySurface` (the record peek) field writes are denied.
 * Picture management passes `writesOnReadOnlySurface` so the peek can still
 * change the picture set.
 */
export const useResourcePermissions = (
  resource: string,
  options?: { writesOnReadOnlySurface?: boolean },
): ResourcePermissions => {
  const { user } = useAuth();
  const readOnly = useIsReadOnlySurface();
  const writesOnReadOnlySurface = options?.writesOnReadOnlySurface === true;

  const roles = useMemo(() => new Set(user?.roles ?? []), [user?.roles]);

  return useMemo(() => {
    const permissions = resourcePermissionsFromRoles(roles, resource);
    return readOnly && !writesOnReadOnlySurface ? withoutWrites(permissions) : permissions;
  }, [readOnly, resource, roles, writesOnReadOnlySurface]);
};
